import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Building2, Plus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CompanyProfilePanel() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [culturePoint, setCulturePoint] = useState("");

  useEffect(() => {
    api.get("/recruiter/company-profile").then((r) => setProfile(r.data)).finally(() => setLoading(false));
  }, []);

  const update = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  const addCulturePoint = () => {
    if (!culturePoint.trim()) return;
    update("culture_points", [...(profile.culture_points || []), culturePoint.trim()]);
    setCulturePoint("");
  };
  const removeCulturePoint = (idx) => update("culture_points", profile.culture_points.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/recruiter/company-profile", profile);
      setProfile(data);
      toast.success("Company profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) return <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto my-10" />;

  return (
    <div className="neu-card rounded-2xl p-6 max-w-2xl" data-testid="recruiter-company-profile-panel">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-indigo-400/15 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-indigo-300" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-white">{profile.company_name}</h3>
          <p className="text-xs text-slate-400">This appears on your job postings and candidate-facing profile</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tagline</label>
          <input value={profile.tagline || ""} onChange={(e) => update("tagline", e.target.value)} placeholder="e.g. Building the future of CRM automation" data-testid="company-profile-tagline-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Description</label>
          <textarea value={profile.description || ""} onChange={(e) => update("description", e.target.value)} rows={4} data-testid="company-profile-description-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Website</label>
            <input value={profile.website || ""} onChange={(e) => update("website", e.target.value)} placeholder="https://..." data-testid="company-profile-website-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Industry</label>
            <input value={profile.industry || ""} onChange={(e) => update("industry", e.target.value)} data-testid="company-profile-industry-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Company Size</label>
            <input value={profile.company_size || ""} onChange={(e) => update("company_size", e.target.value)} placeholder="e.g. 50-200" data-testid="company-profile-size-input" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Culture Highlights</label>
          <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
            {(profile.culture_points || []).map((p, i) => (
              <span key={i} className="flex items-center gap-1 text-[11px] font-semibold text-purple-300 bg-purple-400/10 border border-purple-400/20 rounded-full px-2.5 py-1">
                {p} <button onClick={() => removeCulturePoint(i)}><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={culturePoint} onChange={(e) => setCulturePoint(e.target.value)} placeholder="e.g. Remote-first"
              onKeyDown={(e) => e.key === "Enter" && addCulturePoint()}
              data-testid="company-profile-culture-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button onClick={addCulturePoint} data-testid="company-profile-add-culture-btn" className="bg-white/5 hover:bg-white/10 text-slate-300 px-3 rounded-lg"><Plus className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="company-profile-save-btn" className="w-full mt-5 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Company Profile
      </button>
    </div>
  );
}
